"use client"

import { useMemo, useState } from "react"

import { useAuth } from "@/hooks/useAuth"
import { asArray } from "@/lib/as-array"
import { seriesHex } from "@/lib/stage-colors"
import {
  countPlacementsThisMonth,
  getPlacementCreatedAt,
  getPlacementRecordId,
  placementRevenue,
  revenueMoM,
} from "@/utils/placements"
import { useGetPlacements } from "openapi/api/endpoints"
import type { Placement } from "openapi/api/model"

/** One row of the placements table. */
export type PlacementRow = {
  id: string
  name: string
  initials: string
  /** "Full Time", exactly as the contract stores it. */
  type: string
  jobTitle: string
  company: string
  /** "$28,400" — a machine value, so it renders mono. */
  fee: string
  feeValue: number
  /** "5 Oct 26" */
  from?: string
  /** "open", or the end date. */
  to: string
  /** "permanent", or "183 days". */
  term: string
  splits: string[]
  created: string
  createdAt: number
  placement: Placement
}

const money = (value: number) =>
  `$${Math.round(value).toLocaleString("en-US")}`

/*
  Months are written out rather than formatted, because `month: "short"` gives
  "Sept" in current ICU and the artboard's columns are cut for three letters.
*/
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"]

/** "5 Oct 26" — short, and the same shape as every other date in the product. */
function shortDate(iso?: string | null) {
  if (!iso) return undefined
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return undefined
  return `${date.getDate()} ${MONTHS[date.getMonth()]} ${String(date.getFullYear()).slice(2)}`
}

function initialsOf(name: string) {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? "")
      .join("") || "?"
  )
}

/**
 * The term of a placement, as two lines.
 *
 * The artboard writes a fixed term as its length in days and an open one as
 * "permanent". Both are read off the two dates the contract carries; a
 * placement with no start date says so rather than inventing one.
 */
function describeTerm(placement: Placement) {
  const from = shortDate(placement.start_date)
  const to = shortDate(placement.end_date)

  if (!to) return { from, to: "open", term: "permanent" }

  const start = placement.start_date ? Date.parse(placement.start_date) : Number.NaN
  const end = Date.parse(placement.end_date as string)
  const days =
    Number.isFinite(start) && Number.isFinite(end)
      ? Math.max(0, Math.round((end - start) / 86_400_000))
      : null

  return { from, to, term: days === null ? "fixed term" : `${days} days` }
}

/** The largest value in a list becomes the full bar; the rest are read against it. */
export type PeriodKey = "quarter" | "previous" | "year" | "all"
export type FeeBandKey = "any" | "under10" | "10to25" | "25to50" | "over50"

/** The quarter a date falls in, as its first and last instant. */
function quarterOf(year: number, quarter: number) {
  const start = Date.UTC(year, quarter * 3, 1)
  const end = Date.UTC(year, quarter * 3 + 3, 1)
  return { start, end }
}

/**
 * The four presets 7c offers, resolved against today.
 *
 * The artboard labels them "Q3 / Q2 / Year / All" and writes the range they
 * mean underneath — so the labels are computed rather than written, or they go
 * stale on the first day of October.
 */
export function periodRanges(now = new Date()) {
  const year = now.getUTCFullYear()
  const quarter = Math.floor(now.getUTCMonth() / 3)
  const previousQuarter = quarter === 0 ? 3 : quarter - 1
  const previousYear = quarter === 0 ? year - 1 : year

  const current = quarterOf(year, quarter)
  const previous = quarterOf(previousYear, previousQuarter)

  return {
    quarter: { label: `Q${quarter + 1}`, ...current },
    previous: { label: `Q${previousQuarter + 1}`, ...previous },
    year: {
      label: "Year",
      start: Date.UTC(year, 0, 1),
      end: Date.UTC(year + 1, 0, 1),
    },
    all: {
      label: "All",
      start: Number.NEGATIVE_INFINITY,
      end: Number.POSITIVE_INFINITY,
    },
  } satisfies Record<PeriodKey, { label: string; start: number; end: number }>
}

const FEE_BANDS: Record<FeeBandKey, { label: string; min: number; max: number }> = {
  any: { label: "any", min: 0, max: Number.POSITIVE_INFINITY },
  under10: { label: "< $10k", min: 0, max: 10_000 },
  "10to25": { label: "$10k – $25k", min: 10_000, max: 25_000 },
  "25to50": { label: "$25k – $50k", min: 25_000, max: 50_000 },
  over50: { label: "$50k +", min: 50_000, max: Number.POSITIVE_INFINITY },
}

export const FEE_BAND_OPTIONS = (
  Object.keys(FEE_BANDS) as FeeBandKey[]
).map((value) => ({ value, label: FEE_BANDS[value].label }))

/** "5 Oct 26" from a timestamp, for the window's own ends. */
function dayLabel(at: number) {
  const date = new Date(at)
  return `${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]} ${String(date.getUTCFullYear()).slice(2)}`
}

/**
 * "Apr – Jun 2026", the line 7c writes beside the total.
 *
 * A window open at one end says which end it is open at. Calling a half-open
 * window "all time" was a lie in the direction that matters — the figures under
 * the label really were filtered.
 */
function rangeLabel(start: number, end: number) {
  const openStart = !Number.isFinite(start)
  const openEnd = !Number.isFinite(end)

  if (openStart && openEnd) return "all time"
  // A start after its end selects nothing, and saying so beats a reversed range.
  if (!openStart && !openEnd && start >= end) return "no period"
  if (openStart) return `up to ${dayLabel(end - 1)}`
  if (openEnd) return `since ${dayLabel(start)}`

  const first = new Date(start)
  const last = new Date(end - 1)
  const year = last.getUTCFullYear()
  if (first.getUTCFullYear() !== year) {
    return `${MONTHS[first.getUTCMonth()]} ${first.getUTCFullYear()} – ${MONTHS[last.getUTCMonth()]} ${year}`
  }
  return first.getUTCMonth() === last.getUTCMonth()
    ? `${MONTHS[first.getUTCMonth()]} ${year}`
    : `${MONTHS[first.getUTCMonth()]} – ${MONTHS[last.getUTCMonth()]} ${year}`
}

/**
 * Everything the placements page needs: the rows, the four figures across the
 * top, the two contribution lists, and the period and company filters.
 *
 * Revenue is computed here from `fee_type`, `fee_amount` and `salary` rather
 * than read from the revenue endpoint, so that every figure on the page — the
 * total, the month, the month-on-month, and both lists — comes from the same
 * arithmetic over the same rows. The two used to disagree whenever the endpoint
 * and the list were filtered differently.
 */
export function usePlacements() {
  const [search, setSearch] = useState("")
  const [company, setCompany] = useState("all")
  const [period, setPeriod] = useState<PeriodKey>("all")
  const [fee, setFee] = useState<FeeBandKey>("any")
  const [from, setFrom] = useState("")
  const [to, setTo] = useState("")

  const { data, isLoading, isError } = useGetPlacements()
  const { user } = useAuth()

  const all = useMemo(
    () => asArray<Placement>((data as { placements?: unknown } | undefined)?.placements),
    [data],
  )

  const companies = useMemo(
    () =>
      [...new Set(all.map((p) => p.company_name).filter(Boolean))].sort() as string[],
    [all],
  )

  const ranges = useMemo(() => periodRanges(), [])

  /**
   * The window every figure on the page is computed over.
   *
   * A preset sets it, and the two date chips narrow it further — so "Q3" with a
   * start of 1 August means August and September, which is how a reader expects
   * the two controls to combine.
   */
  const window = useMemo(() => {
    const preset = ranges[period]
    const start = Math.max(preset.start, from ? Date.parse(from) : Number.NEGATIVE_INFINITY)
    // An end date names a whole day, not its first instant.
    const end = Math.min(preset.end, to ? Date.parse(to) + 86_400_000 : Number.POSITIVE_INFINITY)
    return { start, end, label: rangeLabel(start, end) }
  }, [from, period, ranges, to])

  /** The rows the period leaves. */
  const inPeriod = useMemo(() => {
    // Nothing asked for, nothing to narrow. Filtering regardless is what
    // emptied the page: every row was tested against a date the list does not
    // carry under that name, and every row failed.
    if (!Number.isFinite(window.start) && !Number.isFinite(window.end)) return all

    return all.filter((placement) => {
      const at = Date.parse(getPlacementCreatedAt(placement) ?? "")
      /*
        An undated row cannot be placed in or out of a period. It stays, and the
        period simply does not speak to it — dropping it would delete a real
        placement from the page over a missing timestamp.
      */
      if (!Number.isFinite(at)) return true
      return at >= window.start && at < window.end
    })
  }, [all, window])

  const rows = useMemo<PlacementRow[]>(() => {
    const needle = search.trim().toLowerCase()
    const band = FEE_BANDS[fee]
    return inPeriod
      .filter((placement) => company === "all" || placement.company_name === company)
      .filter((placement) => {
        if (fee === "any") return true
        const value = placementRevenue(placement)
        return value >= band.min && value < band.max
      })
      .filter((placement) => {
        if (!needle) return true
        // Split owners too — the field says it searches them, so it does.
        const owners = asArray<{ owner_name?: string }>(placement.splits).map(
          (split) => split.owner_name,
        )
        return [
          placement.candidate_name,
          placement.job_title,
          placement.company_name,
          ...owners,
        ].some((field) => (field ?? "").toLowerCase().includes(needle))
      })
      .map((placement) => {
        const name = placement.candidate_name ?? "Unknown"
        const term = describeTerm(placement)
        const value = placementRevenue(placement)
        return {
          id: getPlacementRecordId(placement),
          name,
          initials: initialsOf(name),
          type: placement.placement_type,
          jobTitle: placement.job_title ?? "—",
          company: placement.company_name ?? "—",
          fee: money(value),
          feeValue: value,
          from: term.from,
          to: term.to,
          term: term.term,
          /*
            The artboard writes the signed-in user's own share as "Me", which is
            how a recruiter reads their own split. Everyone else keeps the name
            the split carries.
          */
          splits: asArray<{ owner_id?: string; owner_name?: string; percentage?: number }>(
            placement.splits,
          ).map((split) => {
            const who =
              split.owner_id && split.owner_id === user?.id
                ? "Me"
                : (split.owner_name ?? "Someone").split(/\s+/)[0]
            return `${who} ${Math.round(split.percentage ?? 0)}%`
          }),
          created: shortDate(getPlacementCreatedAt(placement)) ?? "—",
          createdAt: Date.parse(getPlacementCreatedAt(placement) ?? ""),
          placement,
        }
      })
      .sort((a, b) => b.createdAt - a.createdAt)
  }, [company, fee, inPeriod, search, user?.id])

  /**
   * Revenue split by company, which is what 7c leads with.
   *
   * Every company, ranked, not a top three: the bar is the whole of the money
   * and a legend that omits part of it would not add up to the total printed
   * above it. The largest takes the brand colour, the rest the ramp.
   */
  const breakdown = useMemo(() => {
    const totals = new Map<string, number>()
    /*
      The rows on screen, not the period. Narrowing by company, fee or a search
      term used to move the table and leave the chart where it was, so the two
      halves of the page described different sets of placements — and the total
      over the bar disagreed with the total under it.
    */
    for (const row of rows) {
      const key = row.company
      totals.set(key, (totals.get(key) ?? 0) + row.feeValue)
    }

    const revenue = [...totals.values()].reduce((sum, value) => sum + value, 0)

    const shares = [...totals.entries()]
      /*
        A company that has booked nothing is not a share of anything. Left in,
        it takes a visible sliver off the floor and collects the rounding
        remainder, so a page of zero-fee placements would draw a full bar.
      */
      .filter(([, value]) => value > 0)
      .sort(([, a], [, b]) => b - a)
      .map(([name, value], index) => ({
        id: name,
        label: name,
        amount: money(value),
        share: revenue > 0 ? value / revenue : 0,
        color: seriesHex(index),
      }))

    return { shares, revenue, companyCount: shares.length }
  }, [rows])

  /** The one figure the header pairs with the chart. */
  const momentum = useMemo(() => revenueMoM(all, new Date()), [all])

  return {
    rows,
    /** Every row in the window, before the search, company and fee narrow it. */
    inPeriodCount: inPeriod.length,
    shares: breakdown.shares,
    revenue: money(breakdown.revenue),
    revenueValue: breakdown.revenue,
    companyCount: breakdown.companyCount,
    /** "$84,500 in range" — the money the table currently shows. */
    rangeRevenue: money(rows.reduce((sum, row) => sum + row.feeValue, 0)),
    periodLabel: window.label,
    momentum,
    thisMonth: countPlacementsThisMonth(all, new Date()),
    companies,
    search,
    setSearch,
    company,
    setCompany,
    period,
    setPeriod,
    periodOptions: (Object.keys(ranges) as PeriodKey[]).map((value) => ({
      value,
      label: ranges[value].label,
    })),
    fee,
    setFee,
    from,
    setFrom,
    to,
    setTo,
    clearPeriod: () => {
      setPeriod("all")
      setFee("any")
      setFrom("")
      setTo("")
    },
    hasPeriod: period !== "all" || fee !== "any" || Boolean(from || to),
    total: all.length,
    isLoading,
    isError,
  }
}
